import {
  buildOrganizationNode,
  getSchemaIds,
} from 'utils/structuredData/organization';

const stripUndefined = obj => {
  Object.keys(obj).forEach(k => obj[k] === undefined && delete obj[k]);
  return obj;
};

const toFiniteNumber = v => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

const buildOffer = ({
  lowEstimate,
  highEstimate,
  uniqueEstimate,
  salePrice,
  askingPrice,
  currency,
  isSold,
  url,
  sellerId,
}) => {
  if (!currency) return null;
  const seller = sellerId ? { '@id': sellerId } : undefined;
  const availability = isSold
    ? 'https://schema.org/SoldOut'
    : 'https://schema.org/InStock';

  const numericSale = toFiniteNumber(salePrice);
  const numericUnique = toFiniteNumber(uniqueEstimate);
  const numericLow = toFiniteNumber(lowEstimate);
  const numericHigh = toFiniteNumber(highEstimate);
  const numericAsking = toFiniteNumber(askingPrice);

  if (isSold && numericSale !== null) {
    return stripUndefined({
      '@type': 'Offer',
      price: numericSale,
      priceCurrency: currency,
      availability,
      url,
      seller,
    });
  }

  if (numericUnique !== null) {
    return stripUndefined({
      '@type': 'Offer',
      price: numericUnique,
      priceCurrency: currency,
      availability,
      url,
      seller,
    });
  }

  if (numericLow !== null && numericHigh !== null) {
    return stripUndefined({
      '@type': 'AggregateOffer',
      lowPrice: numericLow,
      highPrice: numericHigh,
      priceCurrency: currency,
      offerCount: 1,
      availability,
      url,
      seller,
    });
  }

  if (numericAsking !== null) {
    return stripUndefined({
      '@type': 'Offer',
      price: numericAsking,
      priceCurrency: currency,
      availability,
      url,
      seller,
    });
  }

  return null;
};

export const buildLotStructuredData = ({
  lotData,
  itemTypename,
  description,
  imageUrl,
  siteUrl,
  siteName,
  siteDescription,
  logoUrl,
  alternateName,
  parentOrganization,
  contactPoint,
  address,
}) => {
  if (!lotData?.slug || !lotData?.item) return null;

  const { orgId } = getSchemaIds(siteUrl);
  const baseUrl = String(siteUrl || '').replace(/\/$/, '');
  const url = `${baseUrl}/lot/${lotData.slug}`;
  const item = lotData.item;
  const isVehicle = itemTypename === 'ContentfulVehicle';
  const isSold = lotData.salePrice != null;
  const currency = lotData.currency || lotData.auction?.currency;
  const auctionName = lotData.auction?.name;

  const offer = buildOffer({
    lowEstimate: lotData.lowEstimate,
    highEstimate: lotData.highEstimate,
    uniqueEstimate: lotData.uniqueEstimate,
    salePrice: lotData.salePrice,
    askingPrice: lotData.askingPrice,
    currency,
    isSold,
    url,
    sellerId: orgId,
  });

  // Without an offer the node can't satisfy Google's Product snippet rule
  // ("offers", "review", or "aggregateRating" required), so downgrade the
  // @type to Thing and drop Product/Vehicle-only fields. Keeps the page in
  // Search; it just isn't evaluated as a Product snippet.
  const hasOffer = !!offer;
  const productType = isVehicle ? 'Vehicle' : 'Product';
  const productIdSuffix = isVehicle ? 'vehicle' : 'product';

  const lotNode = stripUndefined({
    '@type': hasOffer ? productType : 'Thing',
    '@id': `${url}#${hasOffer ? productIdSuffix : 'thing'}`,
    name: item.title || undefined,
    url,
    description: description || undefined,
    image: imageUrl || undefined,
    brand:
      hasOffer && isVehicle && item.make?.name
        ? { '@type': 'Brand', name: item.make.name }
        : undefined,
    model: hasOffer && isVehicle ? item.model || undefined : undefined,
    modelDate:
      hasOffer && isVehicle && item.modelYear
        ? String(item.modelYear)
        : undefined,
    vehicleIdentificationNumber:
      hasOffer && isVehicle ? item.chassis || undefined : undefined,
    vehicleEngine:
      hasOffer && isVehicle && item.engine
        ? { '@type': 'EngineSpecification', name: item.engine }
        : undefined,
    category: auctionName ? `Auction lot — ${auctionName}` : undefined,
  });
  if (offer) lotNode.offers = offer;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      buildOrganizationNode({
        siteUrl,
        siteName,
        description: siteDescription,
        logoUrl,
        alternateName,
        parentOrganization,
        contactPoint,
        address,
      }),
      lotNode,
    ],
  };
};
